import { useEffect, useState } from 'react';
import {
  useInitiateGitHubDeviceAuthMutation,
  usePollGitHubDeviceAuthMutation,
} from '../api/aiImplementationApi';
import { extractErrorMessage } from '../../../utils/errorUtils';
import type { GitHubDeviceFlowInitiateResponse } from '../types';

type UseGitHubDeviceFlowOptions = {
  onSuccess?: () => void;
};

export const useGitHubDeviceFlow = ({ onSuccess }: UseGitHubDeviceFlowOptions = {}) => {
  const [initiateGitHubDeviceAuth, initiateState] = useInitiateGitHubDeviceAuthMutation();
  const [pollGitHubDeviceAuth] = usePollGitHubDeviceAuthMutation();

  const [deviceFlow, setDeviceFlow] = useState<GitHubDeviceFlowInitiateResponse | null>(null);
  const [deviceFlowStatus, setDeviceFlowStatus] = useState<
    'idle' | 'polling' | 'success' | 'error'
  >('idle');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    let polling = true;
    let timeoutId: ReturnType<typeof setTimeout>;

    const poll = async () => {
      if (!polling || deviceFlowStatus !== 'polling' || !deviceFlow) return;

      try {
        const pollRes = await pollGitHubDeviceAuth({
          device_code: deviceFlow.device_code,
        }).unwrap();

        if (pollRes.success) {
          setDeviceFlowStatus('success');
          setDeviceFlow(null);
          setSuccessMessage('GitHub connected successfully!');
          polling = false;
          onSuccess?.();
        } else {
          timeoutId = setTimeout(poll, deviceFlow.interval * 1000);
        }
      } catch (e) {
        setDeviceFlowStatus('error');
        setError(extractErrorMessage(e, 'GitHub authorization failed or timed out.'));
        polling = false;
      }
    };

    if (deviceFlowStatus === 'polling' && deviceFlow) {
      timeoutId = setTimeout(poll, deviceFlow.interval * 1000);
    }

    return () => {
      polling = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [deviceFlow, deviceFlowStatus, pollGitHubDeviceAuth, onSuccess]);

  const startFlow = async () => {
    setError(null);
    setSuccessMessage(null);
    try {
      const response = await initiateGitHubDeviceAuth().unwrap();
      setDeviceFlow(response);
      setDeviceFlowStatus('polling');
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to initiate GitHub connection.'));
    }
  };

  const cancelFlow = () => {
    setDeviceFlowStatus('idle');
  };

  const clearMessages = () => {
    setError(null);
    setSuccessMessage(null);
  };

  return {
    deviceFlow,
    deviceFlowStatus,
    error,
    successMessage,
    isLoading: initiateState.isLoading,
    startFlow,
    cancelFlow,
    clearMessages,
  };
};
